<template>
  <div class="one_step">
    <div class="step_shell">
      <div class="field_wrap">
        <span class="field_label">I would like to:</span>
        <div class="buttons_wrap_3">
          <image-button
            data-test-id="step-0-buy"
            :selected="isSelected('buy')"
            value="buy"
            field="purpose"
            v-on="$listeners"
          />
          <image-button
            data-test-id="step-0-refinance"
            :selected="isSelected('refinance')"
            value="refinance"
            field="purpose"
            v-on="$listeners"
          />
          <image-button
            data-test-id="step-0-both"
            :selected="isSelected('both')"
            value="both"
            field="purpose"
            v-on="$listeners"
          />
        </div>
      </div>

      <div
        class="field_wrap money_wrap"
        v-show="isSelected('buy') || isSelected('both')"
      >
        <span class="field_label">What is the purchase price of the farm?</span>
        <v-currency-field
          data-test-id="step-0-purchase-price"
          outlined
          v-model="Farm_Purchase_Price"
          prefix="$"
          color="#0200b9"
          class="money_field"
          :rules="Farm_Purchase_Price_Rules"
          maxlength="12"
        />
      </div>

      <div
        class="field_wrap money_wrap"
        v-show="isSelected('buy') || isSelected('both')"
      >
        <span class="field_label">How much would you be depositing?</span>
        <v-currency-field
          data-test-id="step-0-deposit"
          outlined
          v-model="Deposit_Amount"
          prefix="$"
          color="#0200b9"
          class="money_field"
          :rules="Deposit_Amount_Rules"
          maxlength="12"
        />
      </div>

      <div
        class="field_wrap money_wrap"
        v-show="isSelected('refinance') || isSelected('both')"
      >
        <span class="field_label"
          >What is the total value of all the farms you own, excluding any
          livestock?</span
        >
        <v-currency-field
          data-test-id="step-0-farm-worth"
          outlined
          v-model="Farm_Worth"
          prefix="$"
          color="#0200b9"
          class="money_field"
          :rules="Farm_Worth_Rules"
          maxlength="12"
        />
      </div>

      <div
        class="field_wrap money_wrap"
        v-show="isSelected('refinance') || isSelected('both')"
      >
        <span class="field_label"
          >How much would you like to refinance your farm for?</span
        >
        <v-currency-field
          data-test-id="step-0-farm-refinance"
          outlined
          v-model="Farm_Refinance_Amount"
          prefix="$"
          color="#0200b9"
          class="money_field"
          :rules="
            isSelected('both')
              ? Farm_Refinance_Both_Amount_Rules
              : Farm_Refinance_Amount_Rules
          "
          maxlength="12"
        />
      </div>

      <div class="loan_error" v-if="showLoanError">{{ loanErrorText }}</div>

      <div class="field_wrap mb_0">
        <span class="field_label"
          >Would you be interested in a revolving credit facility?</span
        >
        <v-radio-group
          data-test-id="step-0-revolving-credit-radio"
          class="radio_place"
          v-model="Working_Capital"
          row
          required
          :rules="radioValidation"
        >
          <v-radio
            v-for="n in 2"
            v-bind:key="'isRevolvingCredit_' + n"
            color="#0200bd"
            :label="getRadioLabel(n)"
            :value="getRadioValue(n)"
            required
          ></v-radio>
        </v-radio-group>
      </div>

      <div class="field_wrap money_wrap" v-if="Working_Capital === 'Yes'">
        <span class="field_label">How much would you want to borrow?</span>
        <v-currency-field
          data-test-id="step-0-revolving-credit-amount"
          outlined
          v-model="Working_Capital_Amount"
          prefix="$"
          color="#0200b9"
          class="money_field"
          maxlength="12"
          :rules="Working_Capital_Amount_Rules"
        />
      </div>

      <transition name="slide-fade">
        <div class="step_1_calculation_results" v-if="showResults">
          <div class="results_line">
            <span class="heading">Total term loan amount</span>
            <span class="results_number main_number">{{
              totalAmount | currency
            }}</span>
          </div>
          <div class="results_line">
            <span class="heading">Monthly interest only repayments*</span>
            <span class="results_number">{{
              interestOnlyRepayment
                | currency({ fractionCount: 2, avoidEmptyDecimals: "" })
            }}</span>
          </div>
          <div class="results_line">
            <span class="heading">Monthly P&amp;I repayments**</span>
            <span class="results_number">{{
              monthlyPIRepayment
                | currency({ fractionCount: 2, avoidEmptyDecimals: "" })
            }}</span>
          </div>
          <div class="disclaimer">
            <p>
              * Based on interest only repayments over a 5-year term at {{interestRate}}%
              p.a.
            </p>
            <p>
              ** Based on principal and interest repayments over a 25-year term
              at {{interestRate}}% p.a.
            </p>
            <p>
              This calculator is a tool to estimate repayments and the total
              loan cost only, and is not an offer of finance by Heartland. The
              total term loan amount does not include any revolving credit.
            </p>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ImageButton from "@/components/ImageButton.vue";
export default {
  components: {
    imageButton: ImageButton,
  },
  props: {
    current: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      product: null,
      interestRate: null, //#pjw get from API rather than hardcoded everywhere
      Working_Capital_Amount: null,
      Deposit_Amount: null,
      Farm_Worth: null,
      Farm_Refinance_Amount: null,
      Farm_Purchase_Price: null,
      Working_Capital: "No",
      valid: false,
      showResults: false,
      Farm_Purchase_Price_Rules: [
        function(v) {
          return (v !== 0 && !!v) || "Please enter a purchase price";
        },
        function(v) {
          return (
            (v && parseInt(v.toString().replace(/,/g, "")) > 49999) ||
            "Minimum purchase price $50,000"
          );
        },
      ],
      Deposit_Amount_Rules: [
        function(v) {
          return (v !== 0 && !!v) || "Please enter a deposit amount";
        },
      ],
      Working_Capital_Amount_Rules: [
        function(v) {
          return (v !== 0 && !!v) || "Please complete this field";
        },
      ],
      Farm_Worth_Rules: [
        function(v) {
          return (v !== 0 && !!v) || "Please complete this field";
        },
      ],
      Farm_Refinance_Amount_Rules: [
        (v) => {
          return (
            (v !== 0 && !!v) ||
            (this.showLoanError ? "" : "Please complete this field")
          );
        },
        (v) => {
          return (
            (v !== null &&
              v !== 0 &&
              parseInt(v.toString().replace(/,/g, "")) > 49999) ||
            ""
          );
        },
      ],
      Farm_Refinance_Both_Amount_Rules: [
        (v) => {
          return (v !== 0 && !!v) || "Please enter a refinance amount";
        },
      ],
    };
  },
  computed: {
    radioValidation: function radioValidation() {
      return [
        function(v) {
          if (v === null) return "Please complete this field";
          return true;
        },
      ];
    },
    totalAmount: function totalAmount() {
      var ta = 0;
      if (this.isSelected("buy")) {
        ta = this.Farm_Purchase_Price - this.Deposit_Amount;
      }
      if (this.isSelected("refinance")) {
        ta = this.Farm_Refinance_Amount;
      }
      if (this.isSelected("both")) {
        ta =
          this.Farm_Purchase_Price -
          this.Deposit_Amount +
          this.Farm_Refinance_Amount;
      }
      return ta;
    },
    interestOnlyRepayment: function interestOnlyRepayment() {
      //return ((0.0445 / 12) * this.totalAmount).toFixed(0); //#pjw get from API
      return ((this.interestRate / 1200) * this.totalAmount).toFixed(0);
    },
    monthlyPIRepayment: function monthlyPIRepayment() {
      //return this.getRepayments(0.0445 / 12, 300, this.totalAmount).toFixed(0);//#pjw get from API
      return this.getRepayments(this.interestRate / 1200, 300, this.totalAmount).toFixed(0);
    },
    lvr: function lvr() {
      var lvr = 0;
      if (this.isSelected("buy")) {
        lvr = this.Farm_Purchase_Price
          ? this.totalAmount / this.Farm_Purchase_Price
          : 0;
      }
      if (this.isSelected("refinance")) {
        lvr = this.Farm_Worth ? this.totalAmount / this.Farm_Worth : 0;
      }
      if (this.isSelected("both")) {
        var temp = this.Farm_Purchase_Price + this.Farm_Worth;
        lvr = temp ? this.totalAmount / temp : 0;
      }
      return lvr;
    },
    Loan_type: function Loan_type() {
      var purpose = "buy";
      if (this.isSelected("refinance")) {
        purpose = "refinance";
      } else if (this.isSelected("both")) {
        purpose = "both";
      }
      return purpose;
    },
    loanInfo: function loanInfo() {
      return {
        Loan_type: this.Loan_type,
        Farm_Purchase_Price: this.Farm_Purchase_Price,
        Deposit_Amount: this.Deposit_Amount,
        Farm_Worth: this.Farm_Worth,
        Farm_Refinance_Amount: this.Farm_Refinance_Amount,
        Working_Capital: this.Working_Capital,
        Working_Capital_Amount: this.Working_Capital_Amount,
        total_amount: this.totalAmount,
        monthly_interest: this.interestOnlyRepayment,
        monthly_repayment: this.monthlyPIRepayment,
      };
    },
    showLoanError: function showLoanError() {
      if (this.isSelected("buy")) {
        return (
          this.totalAmount < 50000 &&
          this.Farm_Purchase_Price &&
          this.Deposit_Amount
        );
      }
      if (this.isSelected("refinance")) {
        return this.totalAmount < 50000 && this.Farm_Refinance_Amount;
      }
      if (this.isSelected("both")) {
        return (
          this.totalAmount < 50000 &&
          this.Farm_Refinance_Amount !== null &&
          this.Farm_Purchase_Price !== null &&
          this.Deposit_Amount !== null &&
          this.Farm_Worth !== null
        );
      }
      return false;
    },
    loanErrorText: function loanErrorText() {
      return "Please adjust the fields above so the loan amount is greater than $50,000";
    },
  },
  updated: function() {
    var isValid = false,
      showRes = false;
    if (this.isSelected("buy")) {
      if (this.Farm_Purchase_Price > 49999 && this.Deposit_Amount !== null) {
        isValid = true;
        showRes = true;
      }
    }
    if (this.isSelected("refinance")) {
      if (this.Farm_Worth !== null && this.Farm_Refinance_Amount > 49999) {
        isValid = true;
        showRes = true;
      }
    }
    if (this.isSelected("both")) {
      if (
        this.Farm_Purchase_Price > 49999 &&
        this.Deposit_Amount !== null &&
        this.Farm_Worth !== null &&
        this.Farm_Refinance_Amount !== null
      ) {
        isValid = true;
        showRes = true;
      }
    }

    this.showResults = showRes && this.totalAmount > 49999;

    if (this.totalAmount < 1) {
      isValid = false;
    }

    if (
      (this.Working_Capital === "Yes" &&
        this.Working_Capital_Amount !== null) ||
      this.Working_Capital === "No"
    ) {
      this.valid = isValid && this.totalAmount > 49999;
      this.$emit("stepValid", { step: 0, valid: this.valid });
    }
    this.$emit("saveLoanData", this.loanInfo);
  },
  watch: {
    lvr: function(value) {
      this.$emit("changeLvr", value);
      this.$emit("stepValid", { step: 0, valid: false });
    },
    Working_Capital: function(newvalue, oldvalue) {
      if (newvalue !== "No") {
        this.valid = false;
        this.$emit("stepValid", { step: 0, valid: false });
      }
    },
  },
  methods: {
    isSelected: function isSelected(str) {
      return this.$parent.$parent.application.purpose === str;
    },
    getRadioLabel: function getRadioLabel(n) {
      return n === 1 ? "Yes" : "No";
    },
    getRadioValue: function getRadioValue(n) {
      return n === 1 ? "Yes" : "No";
    },
    getRepayments: function getRepayments(ir, np, pv) {
      var fv = 0;
      var pmt, pvif;
      fv || (fv = 0);
      if (ir === 0) return -(pv + fv) / np;
      pvif = Math.pow(1 + ir, np);
      pmt = (-ir * pv * (pvif + fv)) / (pvif - 1);
      return Math.abs(pmt);
    },
    getInterestRate: function getInterestRate(app) {
      var that = this;
      var api_url = process.env.API_URL + "/service/rates" + 
        (this.product == "dairy"
          ? "/InterestRates-DairyDirect"
          : "/InterestRates-SheepAndBeef");

      fetch(api_url, {
        method: "GET",
        headers: { "Content-type": "application/json; charset=UTF-8" },
      })
        .then(function(response) {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then(function(data) {
          console.log("Get rates from API: " + data.Data.value);
          app.interestRate = data.Data.value;
          that.interestRate = data.Data.value;
        })
        .catch(function(error) {
          console.warn("Something went wrong.", error);
        });
    },
  },
  created: function() {
    var app = this.$parent.$parent.application;
    var app_loan_data = app.loanData;

    this.product = app.product; //#pjw    
    this.getInterestRate(app);
        
    this.Working_Capital_Amount = app_loan_data.Working_Capital_Amount
      ? app_loan_data.Working_Capital_Amount
      : null;
    this.Deposit_Amount = app_loan_data.Deposit_Amount
      ? app_loan_data.Deposit_Amount
      : null;
    this.Farm_Worth = app_loan_data.Farm_Worth
      ? app_loan_data.Farm_Worth
      : null;
    this.Farm_Refinance_Amount = app_loan_data.Farm_Refinance_Amount
      ? app_loan_data.Farm_Refinance_Amount
      : null;
    this.Farm_Purchase_Price = app_loan_data.Farm_Purchase_Price
      ? app_loan_data.Farm_Purchase_Price
      : null;
    this.Working_Capital = app_loan_data.Working_Capital
      ? app_loan_data.Working_Capital
      : "No";
  },
};
</script>
